import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Table, Progress, Space, DatePicker, Input, Slider, ConfigProvider, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ruRU from 'antd/lib/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import { PredictionItem, ActiveModel } from '../../types';

const { RangePicker } = DatePicker;

// Пауза перед запросом после изменения фильтров, мс
const FILTERS_DEBOUNCE_MS = 400;

dayjs.locale('ru');

interface ExecutionHistoryTableProps {
  selectedModelName?: string | null;
  // API props
  apiPredictions: PredictionItem[];
  loading?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  onTableChange?: (pagination: any, filters: any, sorter: any) => void;
  onFiltersChange?: (filters: {
    dateFrom?: string;
    dateTo?: string;
    email?: string;
    predictionMin?: number;
    predictionMax?: number;
    modelIds?: number[];
    latestOnly?: boolean;
  }) => void;
  activeModels?: ActiveModel[];
}

export const ExecutionHistoryTable: React.FC<ExecutionHistoryTableProps> = ({
  selectedModelName,
  apiPredictions,
  loading = false,
  pagination,
  onTableChange,
  onFiltersChange,
  activeModels = []
}) => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [emailFilter, setEmailFilter] = useState<string>('');
  const [probabilityRange, setProbabilityRange] = useState<[number, number]>([0, 100]);
  const [selectedModels, setSelectedModels] = useState<number[]>([]);
  // По умолчанию — только последний прогноз каждой модели, а не все дни вперемешку
  const [latestOnly, setLatestOnly] = useState<boolean>(true);
  const isFirstMount = useRef(true);

  useEffect(() => {
    if (selectedModelName) {
      // Find model ID by name
      const model = activeModels.find(m => m.name === selectedModelName);
      if (model) {
        setSelectedModels([model.id]);
      }
    }
  }, [selectedModelName, activeModels]);

  useEffect(() => {
    // Skip the first mount to prevent duplicate API call
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    // Only trigger filter changes when filters actually change (not on initial load).
    // Запрос — через паузу: ползунок и ввод email не шлют запрос на каждое движение и букву
    if (!onFiltersChange) return;

    const timer = setTimeout(() => {
      onFiltersChange({
        dateFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: dateRange?.[1]?.format('YYYY-MM-DD'),
        email: emailFilter || undefined,
        predictionMin: probabilityRange[0] > 0 ? probabilityRange[0] : undefined,
        predictionMax: probabilityRange[1] < 100 ? probabilityRange[1] : undefined,
        modelIds: selectedModels.length > 0 ? selectedModels : undefined,
        // Выбран период — показываем все дни в нём
        latestOnly: latestOnly && !dateRange?.[0] && !dateRange?.[1],
      });
    }, FILTERS_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [dateRange, emailFilter, probabilityRange, selectedModels, latestOnly]);

  // API Columns for predictions
  const columns: ColumnsType<PredictionItem> = [
    {
      title: 'Дата предсказания',
      dataIndex: 'prediction_at',
      key: 'prediction_at',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Модель',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 250,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Email пользователя',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Вероятность покупки',
      dataIndex: 'prediction',
      key: 'prediction',
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (prediction: string) => {
        const value = parseFloat(prediction.replace('%', ''));
        return (
          <div>
            <Progress
              percent={value}
              size="small"
              strokeColor={value > 70 ? '#52c41a' : value > 50 ? '#faad14' : '#ff4d4f'}
            />
            <span style={{ marginLeft: 8 }}>{prediction}</span>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <Space wrap size="middle" style={{ width: '100%', marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Дата:</div>
          <ConfigProvider locale={ruRU}>
            <RangePicker
              format="DD.MM.YYYY"
              placeholder={['От', 'До']}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
              style={{ width: 260 }}
            />
          </ConfigProvider>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Модель:</div>
          <Select
            mode="multiple"
            placeholder="Выберите модели"
            value={selectedModels}
            onChange={setSelectedModels}
            style={{ width: 260 }}
            allowClear
            maxTagCount="responsive"
            loading={false}
            notFoundContent="Нет данных"
          >
            {activeModels.map(model => (
              <Select.Option key={model.id} value={model.id}>
                {model.name}{model.is_active === false ? ' (выключена)' : ''}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Показывать:</div>
          <Select
            value={latestOnly ? 'latest' : 'all'}
            onChange={(value) => setLatestOnly(value === 'latest')}
            style={{ width: 220 }}
            disabled={!!(dateRange?.[0] || dateRange?.[1])}
            options={[
              { value: 'latest', label: 'Последний прогноз' },
              { value: 'all', label: 'За все дни' },
            ]}
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Email:</div>
          <Input
            placeholder="Введите email"
            prefix={<SearchOutlined />}
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            style={{ width: 260 }}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
            Вероятность: {probabilityRange[0]}% - {probabilityRange[1]}%
          </div>
          <div style={{ height: 32, display: 'flex', alignItems: 'center' }}>
            <Slider
              range
              min={0}
              max={100}
              value={probabilityRange}
              onChange={(value) => setProbabilityRange(value as [number, number])}
              marks={{
                0: '0%',
                50: '50%',
                100: '100%'
              }}
              style={{ width: 260 }}
            />
          </div>
        </div>
      </Space>

      <Table
        columns={columns}
        dataSource={apiPredictions}
        rowKey={(record) => `${record.prediction_at}-${record.email}-${record.model_name}`}
        scroll={{ x: 1000 }}
        size="small"
        loading={loading}
        onChange={onTableChange}
        pagination={pagination ? {
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Показано ${range[0]}-${range[1]} из ${total}`,
        } : false}
      />
    </div>
  );
};

